import { useEffect, useState } from "react";
import { Circle, Polygon, Polyline, Tooltip } from "react-leaflet";

const R = 6371000;
export function offset([lat, lon], bearingDeg, distM) {
  const b = (bearingDeg * Math.PI) / 180;
  const la = (lat * Math.PI) / 180;
  const d = distM / R;
  const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(b));
  const lo2 = (lon * Math.PI) / 180 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
  return [(la2 * 180) / Math.PI, (lo2 * 180) / Math.PI];
}

function outlinePoly(origin, dir, rows) {
  const left = rows.map(([x, w]) => offset(offset(origin, dir, x), dir - 90, w));
  const right = rows.map(([x, w]) => offset(offset(origin, dir, x), dir + 90, w)).reverse();
  return [origin, ...left, ...right];
}

const colourFor = (c, thr) => (c >= thr ? "#EF4444" : c >= thr * 0.5 ? "#F97316" : c >= thr * 0.1 ? "#FACC15" : "#A3E635");

export function PlumeLayer({ plume }) {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((v) => (v + 1) % 1000), 80);
    return () => clearInterval(id);
  }, []);
  if (!plume) return null;
  const { origin, direction_to_deg: dir, visual_outline: vis, outline, profile, threshold_ugm3: thr, visual_length_m: L } = plume;
  const width = (x) => { const r = vis.find((p) => p[0] >= x) || vis[vis.length - 1]; return r ? r[1] : 50; };
  const conc = (x) => { const r = profile.find((p) => p[0] >= x) || profile[profile.length - 1]; return r ? r[1] : 0; };
  const puffs = Array.from({ length: 14 }, (_, i) => {
    const f = ((i / 14 + t / 400) % 1) ** 1.6;
    const x = Math.max(20, f * L);
    return { key: i, pos: offset(origin, dir, x), r: Math.max(15, width(x) * 0.6), op: 0.35 * (1 - f) + 0.05, col: colourFor(conc(x), thr) };
  });
  return (
    <>
      {vis.length > 1 && <Polygon positions={outlinePoly(origin, dir, vis)} pathOptions={{ color: "#FACC15", weight: 1, dashArray: "4 6", fillColor: "#FACC15", fillOpacity: 0.07 }}>
        <Tooltip sticky>Plume spread (±2σ) · ESTIMATE</Tooltip></Polygon>}
      {outline.length > 1 && <Polygon positions={outlinePoly(origin, dir, outline)} pathOptions={{ color: "#EF4444", weight: 1.5, fillColor: "#EF4444", fillOpacity: 0.25 }}>
        <Tooltip sticky>Ground level above {thr} µg/m³ · ESTIMATE</Tooltip></Polygon>}
      <Polyline positions={[origin, offset(origin, dir, L)]} pathOptions={{ color: "#FDE68A", weight: 1, opacity: 0.6, dashArray: "2 6" }} />
      {plume.max_ground_at_m > 0 && <Circle center={offset(origin, dir, plume.max_ground_at_m)} radius={25} pathOptions={{ color: "#F97316", weight: 2, fillOpacity: 0.8 }}>
        <Tooltip>Max ground level {plume.max_ground_ugm3} µg/m³ at {plume.max_ground_at_m} m · ESTIMATE</Tooltip></Circle>}
      {puffs.map((p) => <Circle key={p.key} center={p.pos} radius={p.r} interactive={false} pathOptions={{ stroke: false, fillColor: p.col, fillOpacity: p.op }} />)}
    </>
  );
}
